import { type AnalyticalFieldAssociationAutomation } from 'modules/payable/models';
import { rejectUnexpectedValue } from 'src/core/utils/switchGuard';

import { type Payable } from '../../../prepare-payables/models';
import {
  type PayableFromRestApi,
  type AutomationFromApi,
  type MemberFromApi,
  type CustomFieldFromApi,
  type BooleanCustomFieldFromApi,
  type ListCustomFieldFromApi,
} from '../../useGetPayable';

// FIXME: we should remove most of the reshaping
export function reshapePayableFromRestApi(
  payable: PayableFromRestApi,
): Payable {
  const reshapeDocumentaryEvidence = (
    documentaryEvidence: PayableFromRestApi['documentaryEvidence'],
  ): Payable['documentaryEvidence'] => {
    if (!documentaryEvidence) {
      return undefined;
    }

    switch (documentaryEvidence.type) {
      case 'invoice':
      case 'perDiemEntry':
      case 'mileageEntry':
      case 'declarationOfMissingInvoice':
        return {
          ...documentaryEvidence,
          invoiceNumber: documentaryEvidence.invoiceNumber,
        };

      case 'creditNote':
        return {
          ...documentaryEvidence,
          creditNoteNumber: documentaryEvidence.creditNoteNumber,
        };

      default:
        return documentaryEvidence;
    }
  };
  return {
    id: payable.id,
    version: payable.version,
    accountingDate: payable.accountingDate ?? null,
    amount: payable.functionalAmount,
    // FIXME: useless
    originalAmount: payable.amount,
    // FIXME: useless renaming
    exchangeRate: payable.functionalExchangeRate,
    description: payable.description,
    // FIXME: wrong renaming
    supplier: reshapeSupplier(payable),
    accountPayableId: payable.accountPayable?.id,
    // FIXME: not a good idea to flatten the discriminated union
    supplierAccount:
      payable.counterparty?.kind === 'supplier'
        ? payable.accountPayable
        : undefined,
    // FIXME: not a good idea to flatten the discriminated union
    employeeAccount:
      payable.counterparty.kind === 'member'
        ? payable.accountPayable
        : undefined,
    team: payable.team,
    vatConfidence:
      payable.automation?.tax && payable.automation?.tax?.isAppliedOnPayable
        ? payable.automation.tax.confidence
        : null,
    itemLines: reshapeItemLines(payable.itemLines),
    type: getPayableType({ type: payable.type, subType: payable.subType }),
    subType: getPayableSubType(payable.subType),
    costCenter: reshapeCostCenter(payable.costCenter),
    customFields: reshapeCustomFields(
      payable.customFields,
      payable.automation?.analyticalFieldAssociations,
    ),
    state: reshapePayableState(payable.state),
    mileageDetails: payable.mileage,
    perDiem: payable.perDiem,
    automation: reshapeAutomation(payable.automation),
    // FIXME: createdAt has a totally different meaning
    createdAt: payable.creationDate,
    creationDate: payable.creationDate,
    user: reshapeMember(payable.member),
    counterparty: reshapeCounterparty(payable),
    // FIXME: useless, wrong value, we should always leverage switch +
    prepared:
      payable.state !== 'unprepared' &&
      payable.state !== 'created' &&
      payable.state !== 'notBookkept',
    invoiceNumber:
      payable.invoiceNumber ??
      (payable.automation?.documentaryEvidenceNumber &&
      payable.automation.documentaryEvidenceNumber.isAppliedOnPayable
        ? payable.automation.documentaryEvidenceNumber.documentaryEvidenceNumber
        : undefined),
    documentaryEvidence: reshapeDocumentaryEvidence(
      payable.documentaryEvidence,
    ),
    amortisation: payable.amortisation
      ? {
          date: {
            from: payable.amortisation.date.from,
            to: payable.amortisation.date.to,
          },
          schemeId: payable.amortisation.schemeId,
          schemeName: payable.amortisation.schemeName,
        }
      : undefined,
    expenseBeneficiaries: payable.expenseBeneficiaries,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function reshapeAutomation(
  rawAutomation: AutomationFromApi | undefined,
): Payable['automation'] | undefined {
  if (rawAutomation === undefined) {
    return undefined;
  }

  const automation: Payable['automation'] = {
    analyticalFieldAssociations: (
      rawAutomation.analyticalFieldAssociations ?? []
    ).map((afaAutomation) =>
      afaAutomation.kind === 'ruleBased'
        ? { ...afaAutomation, fieldEntityValueId: afaAutomation.output }
        : afaAutomation,
    ),
  };

  if (rawAutomation.tax) {
    if (rawAutomation.tax.kind === 'default') {
      automation.tax = {
        kind: 'default',
        isAppliedOnPayable: !!rawAutomation.tax.isAppliedOnPayable,
      };
    } else if (rawAutomation.tax.kind === 'recommendation') {
      automation.tax = {
        kind: 'recommendation',
        confidence: rawAutomation.tax.confidence ?? undefined,
        isAppliedOnPayable: !!rawAutomation.tax.isAppliedOnPayable,
      };
    } else {
      automation.tax = {
        kind: 'prediction',
        taxAccount: rawAutomation.tax.taxAccount ?? {
          id: '',
          name: '',
        },
        confidence: rawAutomation.tax.confidence ?? undefined,
        isAppliedOnPayable: !!rawAutomation.tax.isAppliedOnPayable,
      };
    }
  }

  if (rawAutomation.expenseAccount) {
    if (
      rawAutomation.expenseAccount.kind === 'default' ||
      rawAutomation.expenseAccount.kind === 'ruleBased' ||
      rawAutomation.expenseAccount.kind === 'expenseCategoryRule'
    ) {
      automation.expenseAccount = rawAutomation.expenseAccount;
    } else if (rawAutomation.expenseAccount.kind === 'prediction') {
      automation.expenseAccount = {
        kind: rawAutomation.expenseAccount.kind,
        expenseAccount: rawAutomation.expenseAccount.expenseAccount ?? {
          id: '',
          name: '',
        },
        confidence: rawAutomation.expenseAccount.confidence ?? 0,
        isAppliedOnPayable: !!rawAutomation.expenseAccount.isAppliedOnPayable,
      };
    }
  }

  if (rawAutomation.vatAccount) {
    automation.vatAccount = rawAutomation.vatAccount;
  }

  if (rawAutomation.expenseAmount) {
    automation.expenseAmount = {
      kind: rawAutomation.expenseAmount.kind,
      confidence: rawAutomation.expenseAmount.confidence,
      isAppliedOnPayable: !!rawAutomation.expenseAmount.isAppliedOnPayable,
    };
  }

  if (rawAutomation.accountPayable) {
    automation.accountPayable = {
      accountPayableMatch: {
        id: rawAutomation.accountPayable.accountPayableMatch.id,
        generalAccountCode:
          rawAutomation.accountPayable.accountPayableMatch.generalAccountCode,
        isDefault: rawAutomation.accountPayable.accountPayableMatch.isDefault,
        isArchived: rawAutomation.accountPayable.accountPayableMatch.isArchived,
      },
      isAppliedOnPayable: !!rawAutomation.accountPayable.isAppliedOnPayable,
    };
  }

  if (rawAutomation.documentaryEvidenceDate) {
    automation.documentaryEvidenceDate = {
      ...rawAutomation.documentaryEvidenceDate,
      documentaryEvidenceDate: rawAutomation.documentaryEvidenceDate
        .documentaryEvidenceDate
        ? new Date(
            rawAutomation.documentaryEvidenceDate.documentaryEvidenceDate,
          )
        : undefined,
      isAppliedOnPayable:
        !!rawAutomation.documentaryEvidenceDate.isAppliedOnPayable,
    };
  }

  if (rawAutomation.documentaryEvidenceNumber) {
    automation.documentaryEvidenceNumber = {
      ...rawAutomation.documentaryEvidenceNumber,
      isAppliedOnPayable:
        !!rawAutomation.documentaryEvidenceNumber.isAppliedOnPayable,
    };
  }

  return automation;
}

function reshapeCostCenter(
  costCenter: PayableFromRestApi['costCenter'],
): Payable['costCenter'] {
  if (!costCenter) {
    return undefined;
  }

  return {
    id: costCenter.id,
    name: costCenter.name,
  };
}

export function getPayableType({
  type,
  subType,
}: {
  type: PayableFromRestApi['type'];
  subType: PayableFromRestApi['subType'];
}): Payable['type'] {
  switch (type) {
    case 'mileageExpense':
      return 'mileage_allowance';
    case 'perDiemExpense':
      return 'per_diem_allowance';
    case 'claimedBill':
      return 'expense_claim';
    case 'bill':
      return [
        'physical',
        'multiUseCard',
        'singlePurchase',
        'subscription',
      ].includes(subType)
        ? 'card_purchase'
        : 'invoice_purchase';
    case 'reverseBill':
      return 'reversal';
    default:
      return rejectUnexpectedValue('type', type);
  }
}

export function getPayableSubType(
  subType: PayableFromRestApi['subType'],
): Payable['subType'] {
  switch (subType) {
    case 'physical':
      return 'plasticCard';
    case 'singlePurchase':
      return 'singleUseCard';
    case 'multiUseCard':
      return 'multiUseCard';
    case 'subscription':
      return 'subscriptionCard';
    case 'creditNote':
      return 'creditNote';
    default:
      return undefined;
  }
}

function reshapeCounterparty(
  payable: PayableFromRestApi,
): Payable['counterparty'] {
  if (payable.counterparty.kind === 'member') {
    if (payable.counterparty.email) {
      return {
        id: payable.counterparty.id,
        givenName: payable.counterparty.givenName,
        familyName: payable.counterparty.familyName,
        email: payable.counterparty.email,
        avatar: payable.counterparty.avatar ?? undefined,
      };
    }
    return {
      id: payable.counterparty.id,
      givenName: payable.counterparty.givenName,
      familyName: payable.counterparty.familyName,
      avatar: undefined,
    };
  }
  return {
    id: payable.counterparty.id,
    name: payable.counterparty.name,
    thumbnailUrl: payable.counterparty.thumbnailUrl,
  };
}

function reshapeMember(member: MemberFromApi): Payable['user'] {
  return {
    id: member.id,
    givenName: member.givenName,
    familyName: member.familyName,
    email: member.email ?? '',
    avatar: member.avatar ?? '',
  };
}

function reshapeItemLines(
  itemLines: PayableFromRestApi['itemLines'],
): Payable['itemLines'] {
  return itemLines.map((itemLine, index) => {
    const expense = {
      amount: itemLine.netAmount,
      expenseAccount: itemLine.expenseAccount
        ? {
            id: itemLine.expenseAccount.id,
            name: itemLine.expenseAccount.name,
          }
        : null,
    };

    const vat = {
      amount: itemLine.vatAmount,
      vatAccount: itemLine.taxAccount
        ? {
            id: itemLine.taxAccount.id,
            name: itemLine.taxAccount.name,
            rate:
              itemLine.taxAccount.type === 'vat'
                ? Number(itemLine.taxAccount.rate)
                : -1,
          }
        : null,
    };

    return {
      id: index,
      vat,
      expense,
      analyticalFieldAssociations: itemLine.analyticalFieldAssociations,
      grossAmount: itemLine.grossAmount,
      taxAccountId: itemLine.taxAccountId ?? null,
      expenseAccountId: itemLine.expenseAccountId ?? null,
    };
  });
}

export function reshapeSupplier(
  payable: PayableFromRestApi,
): Payable['supplier'] {
  if (payable.counterparty?.kind === 'supplier') {
    return payable.counterparty;
  }

  if (
    payable.commitmentCounterparty &&
    payable.commitmentCounterparty?.kind === 'supplier'
  ) {
    return payable.commitmentCounterparty;
  }

  return undefined;
}

export function reshapeCustomFields(
  customFields: CustomFieldFromApi[],
  analyticalFieldAssociationAutomation?: AnalyticalFieldAssociationAutomation[],
): Record<string, string | boolean> {
  const reshapedCustomFields: Record<string, string | boolean> = {};

  customFields?.forEach((customFieldAssociation) => {
    if (isBooleanCustomField(customFieldAssociation)) {
      const { id } = customFieldAssociation.customField;
      const { value } = customFieldAssociation;

      // TODO @finance-accountant: remove this hack
      // We remove automated boolean custom fields as we cannot handle them yet
      // They are only removed from custom fields as they are not splittable
      const isAutomated = analyticalFieldAssociationAutomation?.some(
        (association) =>
          association.fieldEntityId === id && association.isAppliedOnPayable,
      );
      if (!isAutomated) {
        reshapedCustomFields[id] = value;
      }
    } else if (isListCustomField(customFieldAssociation)) {
      const { id } = customFieldAssociation.customField;
      const { values } = customFieldAssociation;
      const { id: value } = values[0];

      reshapedCustomFields[id] = value;
    }
  });

  return reshapedCustomFields;
}

function isBooleanCustomField(
  customField: CustomFieldFromApi,
): customField is BooleanCustomFieldFromApi {
  return 'value' in customField && customField.value !== undefined;
}

function isListCustomField(
  customField: CustomFieldFromApi,
): customField is ListCustomFieldFromApi {
  return 'values' in customField && customField.values !== undefined;
}

const reshapePayableState = (
  rawType: PayableFromRestApi['state'],
): Payable['state'] => {
  switch (rawType) {
    case 'created':
      return 'created';
    case 'prepared':
      return 'prepared';
    case 'notBookkept':
      return 'notBookkept';
    case 'unprepared':
      return 'unprepared';
    case 'discarded':
      return 'discarded';
    case 'to_accounting:pending':
      return 'toAccountingPending';
    case 'to_accounting:failed':
      return 'toAccountingFailed';
    case 'in_accounting':
      return 'inAccounting';
    case 'in_accounting:manually':
      return 'inAccountingManually';
    default:
      throw new Error(`Unknown payable type ${rawType}`);
  }
};
